import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import CustomSelect from 'components/atoms/custom-select/CustomSelect';
import {
  createGetScDepartmentOptions,
  createGetScDepartmentsPopupOptions,
} from 'queries/departmentQueries';

function AddDepartment({ children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Modal container */}
      <div className="w-[600px] max-w-[90%] rounded-lg bg-white p-6 shadow-lg">
        {children}
      </div>
    </div>
  );
}

AddDepartment.Header = function AddDepartmentHeader({ title }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-xl font-semibold">{title}</h2>
    </div>
  );
};

AddDepartment.ButtonRow = function AddDepartmentButtonRow({ children }) {
  return <div className="mt-6 flex justify-between">{children}</div>;
};

function DepartmentRow({
  department,
  pecrId,
  siteId,
  onSelectChange,
  selectedValue,
}) {
  const [isFocused, setIsFocused] = useState(false);

  const { data } = useQuery(
    createGetScDepartmentsPopupOptions(
      {
        pecrId: pecrId,
        SiteId: siteId,
        DepartmentID: department.departmentId,
      },
      {
        enabled: isFocused,
      }
    )
  );

  const options = data
    ? data.map(item => ({
        value: item.OwnerID || item.ID || item.OwnerName,
        label: item.OwnerName,
        ...item,
        departmentId: department.departmentId,
        departmentName: department.departmentName,
        subDepartmentName: department.subDepartmentName,
        eIndex: department.eIndex,
      }))
    : [];

  return (
    <div>
      <label
        htmlFor={`dept-${department.id}`}
        className="mb-1 block text-sm font-medium text-gray-700"
      >
        {department.departmentName}
      </label>
      <CustomSelect
        name={'SiteCoordinator'}
        id={`dept-${department.id}`}
        options={options}
        value={selectedValue}
        onChange={option => onSelectChange(option)}
        onFocus={() => setIsFocused(true)}
        placeholder="Select..."
        isClearable
      />
    </div>
  );
}

AddDepartment.Departments = function AddDepartmentDepartments({
  siteId,
  pecrId,
  setSelectedDepartments,
  selectedDepartments = {},
  estimateData,
}) {
  const { data } = useQuery(
    createGetScDepartmentOptions(
      { SiteId: siteId, pecrId },
      {
        enabled: !!siteId && !!pecrId,
      }
    )
  );

  function handleDepartmentSelection(item, selectedDepartments) {
    if (item.Name === 'Compliance') {
      return {
        [item.departmentId]: {
          compliance: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'EH&S') {
      return {
        [item.departmentId]: {
          ehs: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Finance') {
      return {
        [item.departmentId]: {
          finance: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Aftermarket') {
      return {
        [item.departmentId]: {
          aftermarket: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Industrial Process') {
      return {
        [item.departmentId]: {
          industrialprocess: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Literature') {
      return {
        [item.departmentId]: {
          literature: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Logistics / Supply Chain') {
      return {
        [item.departmentId]: {
          logistics: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Marketing') {
      return {
        [item.departmentId]: {
          marketing: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Purchasing') {
      return {
        [item.departmentId]: {
          purchasing: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Sales') {
      return {
        [item.departmentId]: {
          sales: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Sales Tools') {
      return {
        [item.departmentId]: {
          salestools: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Engineering') {
      let labelKey;
      if (item.eIndex === 1) {
        labelKey = 'Engineering1OwnerID';
      }
      if (item.eIndex === 2) {
        labelKey = 'Engineering2OwnerID';
      }
      if (item.eIndex === 3) {
        labelKey = 'Engineering3OwnerID';
      }
      if (item.eIndex === 4) {
        labelKey = 'Engineering4OwnerID';
      }
      if (selectedDepartments[item.departmentId]) {
        return {
          [item.departmentId]: {
            ...selectedDepartments[item.departmentId],
            [labelKey]: item.value ? item.value : '',
          },
        };
      }
      return {
        [item.departmentId]: {
          Engineering1Label: item.Engineering1Label,
          Engineering2Label: item.Engineering2Label,
          Engineering3Label: item.Engineering3Label,
          Engineering4Label: item.Engineering4Label,
          Engineering1OwnerID: null,
          Engineering2OwnerID: null,
          Engineering3OwnerID: null,
          Engineering4OwnerID: null,
          [labelKey]: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Quality') {
      let labelKey;
      if (item.eIndex === 1) {
        labelKey = 'Quality1OwnerID';
      }
      if (item.eIndex === 2) {
        labelKey = 'Quality2OwnerID';
      }
      if (item.eIndex === 3) {
        labelKey = 'Quality3OwnerID';
      }
      if (item.eIndex === 4) {
        labelKey = 'Quality4OwnerID';
      }
      if (selectedDepartments[item.departmentId]) {
        return {
          [item.departmentId]: {
            ...selectedDepartments[item.departmentId],
            [labelKey]: item.value ? item.value : '',
          },
        };
      }
      return {
        [item.departmentId]: {
          Quality1Label: item.Quality1Label,
          Quality2Label: item.Quality2Label,
          Quality3Label: item.Quality3Label,
          Quality4Label: item.Quality4Label,
          Quality1OwnerID: null,
          Quality2OwnerID: null,
          Quality3OwnerID: null,
          Quality4OwnerID: null,
          [labelKey]: item.value ? item.value : '',
        },
      };
    } else if (item.Name === 'Manufacturing') {
      let labelKey;
      if (item.eIndex === 1) {
        labelKey = 'Manufacturing1OwnerID';
      }
      if (item.eIndex === 2) {
        labelKey = 'Manufacturing2OwnerID';
      }
      if (item.eIndex === 3) {
        labelKey = 'Manufacturing3OwnerID';
      }
      if (item.eIndex === 4) {
        labelKey = 'Manufacturing4OwnerID';
      }
      if (selectedDepartments[item.departmentId]) {
        return {
          [item.departmentId]: {
            ...selectedDepartments[item.departmentId],
            [labelKey]: item.value ? item.value : '',
          },
        };
      }
      return {
        [item.departmentId]: {
          Manufacturing1Label: item.Manufacturing1Label,
          Manufacturing2Label: item.Manufacturing2Label,
          Manufacturing3Label: item.Manufacturing3Label,
          Manufacturing4Label: item.Manufacturing4Label,
          Manufacturing1OwnerID: null,
          Manufacturing2OwnerID: null,
          Manufacturing3OwnerID: null,
          Manufacturing4OwnerID: null,
          [labelKey]: item.value ? item.value : '',
        },
      };
    }
  }

  // Get list of existing department names from estimateData
  const existingDeptNames = estimateData
    ? estimateData.map(item => item.Dept)
    : [];

  const allDepartmentOptions = data
    ? [
        ...new Map(
          data.map(item => [`${item.DepartmentID}-${item.EIndex}`, item])
        ).values(),
      ]
        .map(dept => ({
          id: `${dept.DepartmentID}-${dept.EIndex}`,
          departmentId: dept.DepartmentID,
          eIndex: dept.EIndex,
          departmentName: dept.Label
            ? `${dept.Department} <${dept.Label}>`
            : dept.Department,
          subDepartmentName: dept.Label,
        }))
        .filter(dept => !existingDeptNames.includes(dept.departmentName))
    : [];

  function handleSelectChange(deptId, selectedOption, department) {
    if (!selectedOption) {
      const departmentId = department.departmentId;
      const eIndex = department.eIndex;
      const deptName = department.departmentName.split(' <')[0];

      if (['Engineering', 'Quality', 'Manufacturing'].includes(deptName)) {
        const labelKey = `${deptName}${eIndex}OwnerID`;
        setSelectedDepartments(prev => {
          const newState = { ...prev };
          delete newState[deptId];
          if (newState[departmentId]) {
            newState[departmentId] = {
              ...newState[departmentId],
              [labelKey]: null,
            };
          }
          return newState;
        });
      } else {
        setSelectedDepartments(prev => {
          const newState = { ...prev };
          delete newState[deptId];
          delete newState[departmentId];
          return newState;
        });
      }
      return;
    }

    const departmentData = handleDepartmentSelection(
      selectedOption,
      selectedDepartments
    );
    setSelectedDepartments(prev => ({
      ...prev,
      [deptId]: selectedOption,
      ...departmentData,
    }));
  }

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-gray-700">
        Select Departments
      </p>
      <div className="max-h-[400px] overflow-y-auto rounded border border-gray-200 p-3">
        <div className="grid grid-cols-2 gap-4">
          {allDepartmentOptions.map(dept => (
            <DepartmentRow
              key={dept.id}
              department={dept}
              siteId={siteId}
              pecrId={pecrId}
              selectedValue={selectedDepartments[dept.id] || null}
              onSelectChange={option =>
                handleSelectChange(dept.id, option, dept)
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddDepartment;
